<script setup>
import { ref, inject, computed } from 'vue'
import Popup from '@/components/Popup.vue'
import personaCreatorCategories from '@/configs/personaCreator.js'
import SvgIcon from '@/components/SvgIcon.vue'
import ButtonComponent from '@/components/ButtonComponent.vue'
import { useToast } from 'vue-toast-notification'
import { useLoading } from 'vue-loading-overlay'
import shuffleArray from '@/helpers/shuffleArray.js'

const props = defineProps({
  open: Boolean,
  modelName: String,
  modelGender: String,
})

const emit = defineEmits(['update:open', 'persona-expanded'])

const personaDescription = ref('')

const selectedTagsCategory = ref(personaCreatorCategories[0].name)

function insertTag(category, tag) {
  if (category === 'Hobbies') {
    tag = `${tag} hobby`
  } else if (category === 'Career') {
    tag = `${tag} career`
  } else if (category === 'Personality') {
    tag = `${tag} personality`
  } else if (category === 'Relationship') {
    tag = `Is your ${tag}`
  } else if (category === 'Family') {
    tag = `${tag} family situation`
  } else if (category === 'Fetishes') {
    tag = `${tag} fetish`
  }

  const trimmedPersona = personaDescription.value.trim()
  const personaEmpty = trimmedPersona.length === 0
  const lastChar = trimmedPersona.slice(-1)

  personaDescription.value =
    trimmedPersona +
    (personaEmpty ? tag : lastChar === ',' ? ` ${tag}` : `, ${tag}`)
}

const $toast = useToast()

const $axios = inject('axios')

const $loading = useLoading({
  canCancel: false,
  isFullPage: true,
  color: '#a15cff',
})

async function expandPersona() {
  if (!personaDescription.value) {
    $toast.error('Please enter a persona')
    return
  }

  const loader = $loading.show()

  let expandedPersona = null
  try {
    expandedPersona = await $axios
      .post('/persona/expand-persona', {
        personaDescription: personaDescription.value,
        modelName: props.modelName,
        modelGender: props.modelGender,
      })
      .then((res) => res.data.persona)
  } catch (e) {
    $toast.error('Unexpected error occured')
    loader.hide()
    throw e
  }

  loader.hide()

  emit('persona-expanded', expandedPersona)
}

const tagsShown = computed(() =>
  shuffleArray(
    personaCreatorCategories.find((c) => c.name === selectedTagsCategory.value)
      .tags,
  ),
)
</script>

<template>
  <Popup
    class="sticky z-40"
    popupStyle="bg-[#0A0D22] max-w-[1225px] w-full pt-[50px] pb-5 px-[30px] lg:py-[75px] mx-4 lg:mx-0 lg:px-[122px]"
    :open="open"
    @update:open="emit('update:open', $event)"
  >
    <h2 class="text-[25px] lg:text-[30px] leading-1 mb-4 mt-2.5 lg:mt-0">
      Persona Creator
    </h2>
    <p class="mb-4">
      This tool helps you create a custom persona without having to come up with
      all the details. Just type a brief description on the box below, and our
      AI will create a detailed persona for you.
    </p>
    <div></div>
    <textarea
      v-model="personaDescription"
      name="Persona"
      rows="4"
      class="bg-[#0A0D22] placeholder-[#B1B5DB] py-[22px] px-[18px] rounded-[15px] border border-[#141A3D] w-full mx-auto resize-none"
      placeholder="Write your high level description here"
    ></textarea>
    <div
      class="grid grid-cols-3 lg:flex gap-[6px] bg-[#0A0D22] my-[17px] p-[7px] rounded-[15px] border border-[#161929]"
    >
      <button
        @click="selectedTagsCategory = category.name"
        v-for="category in personaCreatorCategories"
        class="lg:flex-1 lg:bg-transparent flex justify-center items-center bg-[#141939] py-[13px] rounded-[10px] border border-transparent"
        :class="category.name === selectedTagsCategory ? 'bg-[]' : ''"
        :style="
          category.name === selectedTagsCategory
            ? {
                background:
                  'radial-gradient(255.14% 174.74% at 38.76% 155.71%, #CC47FF 0%, #9A5CFF 100%)',
                boxShadow:
                  '0px 0px 0px 1px #B852FF, 0px -2px 0px 0px #A831FD inset',
                border: '1px solid',
                borderImageSource:
                  'linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(153, 153, 153, 0.1) 100%)',
              }
            : {}
        "
      >
        <p class="text-sm">{{ category.name }}</p>
      </button>
    </div>
    <div class="overflow-y-auto">
      <div class="grid grid-rows-3 grid-flow-col gap-[7px]">
        <button
          v-for="tag in tagsShown"
          class="w-auto flex items-center gap-[10px] bg-[#0E132D] p-1 pr-5 rounded-[100px] border border-[#141A3D] border-opacity-70 whitespace-nowrap"
          @click="insertTag(selectedTagsCategory, tag)"
        >
          <div class="bg-white bg-opacity-5 p-[7px] rounded-full">
            <SvgIcon icon-id="plus" class="w-[18px] h-[18px]" alt="plus icon" />
          </div>
          <p class="text-sm">{{ tag }}</p>
        </button>
      </div>
    </div>

    <ButtonComponent
      :dropShadow="false"
      class="w-full mt-10 px-3.5 py-[14px] rounded-[15px] flex items-center justify-center gap-1.5"
      @click="expandPersona"
    >
      <SvgIcon icon-id="magic-wand" class="w-5 h-5"> </SvgIcon>
      Create
    </ButtonComponent>
  </Popup>
</template>
